import {getCurrentLevel} from '@cdo/apps/code-studio/progressReduxSelectors';
import {LabState} from '@cdo/apps/lab2/lab2Redux';
import {
  getAppOptionsEditBlocks,
  getAppOptionsEditingExemplar,
  getAppOptionsViewingExemplar,
} from '@cdo/apps/lab2/projects/utils';
import {RootState} from '@cdo/apps/types/redux';
import {LevelStatus} from '@cdo/generated-scripts/sharedConstants';

// Redux selectors for lab2 state.

// If any load is currently in progress.
export const isLabLoading = (state: {lab: LabState}) =>
  state.lab.isLoadingProjectOrLevel ||
  state.lab.isLoading ||
  state.lab.isLoadingTheme;

// If there is an error present on the page.
export const hasPageError = (state: {lab: LabState}) => {
  return state.lab.pageError !== undefined;
};

export const isProjectTemplateLevel = (state: {lab: LabState}) =>
  !!state.lab.levelProperties?.projectTemplateLevelName;

// Returns true if the workspace is permanently read-only.
// This excludes temporary read-only states such as running/validating.
export const isPermanentlyReadOnlyWorkspace = (state: RootState) => {
  const isEditMode = !!getAppOptionsEditBlocks();
  const isEditingExemplar = getAppOptionsEditingExemplar();
  const isViewingExemplar = getAppOptionsViewingExemplar();
  const isWidgetView = !!state.lab.levelProperties?.widgetView;

  // Exemplar and block edit modes do not have a channel.
  if (isEditMode || isEditingExemplar) {
    return false;
  } else if (isViewingExemplar) {
    return true;
  }
  // Otherwise, we are in permanently read-only mode if we are not the owner of the channel,
  // the level is frozen, or in widget view.
  const isOwner = state.lab.channel?.isOwner;
  const isFrozen = !!state.lab.channel?.frozen;

  return !isOwner || isFrozen || isWidgetView;
};

// Returns true if the workspace is temporarily read-only.
// This includes runtime and version states, but excludes permanent states like ownership.
export const isTemporarilyReadOnlyWorkspace = (state: RootState) => {
  const isEditMode = !!getAppOptionsEditBlocks();
  const isEditingExemplar = getAppOptionsEditingExemplar();

  // Edit modes should always be editable.
  if (isEditMode || isEditingExemplar) {
    return false;
  }

  const hasSubmitted = getCurrentLevel(state)?.status === LevelStatus.submitted;
  const isViewingOldVersion = state.lab2Project.viewingOldVersion;
  const isAiTutorVersion = state.lab2Project.viewingAiTutorVersion;
  const readOnlyPredictLevel = isReadOnlyPredictLevel(state);

  // Also check for temporary read-only state (running/validating).
  const isRunningAndReadonly =
    (state.lab2System.isRunning || state.lab2System.isValidating) &&
    shouldBeReadonlyWhileRunning(state);

  return (
    isRunningAndReadonly ||
    hasSubmitted ||
    isViewingOldVersion ||
    isAiTutorVersion ||
    readOnlyPredictLevel
  );
};

// This may depend on more factors, such as share.
export const isReadOnlyWorkspace = (state: RootState) =>
  isPermanentlyReadOnlyWorkspace(state) ||
  isTemporarilyReadOnlyWorkspace(state);

// If the level should show an exemplar link, the exemplar link is the current url
// with the query parameter exemplar=true at the end.
export const getExampleSolutionLink = (state: RootState) => {
  if (state.lab.levelProperties?.showExemplarLink) {
    const url = new URL(window.location.href);
    url.searchParams.set('exemplar', 'true');
    return [url.toString()];
  }
  return [];
};

// Helper functions

// Returns if the current state represents a predict level that should be read only.
// If the predict level code is not editable after submit or the user has not submitted a response,
// the predict level is read only.
export function isReadOnlyPredictLevel(state: RootState) {
  const isPredictLevel =
    state.lab.levelProperties?.predictSettings?.isPredictLevel || false;
  let isReadOnlyPredictLevel = isPredictLevel;
  if (isPredictLevel) {
    const isEditableAfterSubmit =
      state.lab.levelProperties?.predictSettings?.codeEditableAfterSubmit ||
      false;
    const hasSubmittedPredictResponse = state.predictLevel.hasSubmittedResponse;
    // If the predict level code is not editable after submit or the user has not submitted a response,
    // the predict level is read only.
    isReadOnlyPredictLevel =
      !isEditableAfterSubmit || !hasSubmittedPredictResponse;
  }
  return isReadOnlyPredictLevel;
}

export const isViewingAiTutorVersionFileUpdates = (state: RootState) =>
  !!state.lab2Project?.viewingAiTutorVersion &&
  !!state.lab2Project?.aiTutorVersionFiles?.length;

// Currently only Python Lab disables editing while code is running.
function shouldBeReadonlyWhileRunning(state: RootState) {
  return state.lab.levelProperties?.appName === 'pythonlab';
}
